import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, GeoJSON, LayersControl, useMapEvents } from "react-leaflet";
import L from "leaflet";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "leaflet/dist/leaflet.css";
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});



const ClickHandler = ({ setClickedData }) => {
    useMapEvents({
        click: async (e) => {
            const { lat, lng } = e.latlng;
            const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&hourly=temperature_2m,cloud_cover,cape,cloud_cover_low,cloud_cover_mid,cloud_cover_high,wind_speed_10m,wind_direction_10m,rain&models=ecmwf_ifs025`;

            try {
                const response = await fetch(url);
                const data = await response.json();

                const hourlyData = data.hourly.time.map((time, index) => ({
                    time,
                    temperature_2m: data.hourly.temperature_2m[index],
                    cloud_cover: data.hourly.cloud_cover[index],
                    cape: data.hourly.cape[index],
                    cloud_cover_low: data.hourly.cloud_cover_low[index],
                    cloud_cover_mid: data.hourly.cloud_cover_mid[index],
                    cloud_cover_high: data.hourly.cloud_cover_high[index],
                    wind_speed_10m: data.hourly.wind_speed_10m[index] * 3.6, // convert m/s to km/h
                    wind_direction_10m: data.hourly.wind_direction_10m[index],
                    rain: data.hourly.rain[index],
                }));

                setClickedData({ lat, lng, chartData: hourlyData });
            } catch (err) {
                console.error("Error fetching weather data:", err);
            }
        },
    });

    return null;
};



const OpenmeteoJMA = () => {
    const [clickedData, setClickedData] = useState(null);
    const center = [-8.95, 125.50];
    const [boundaryData, setBoundaryData] = useState(null);
    const [municipalityData, setMunicipalityData] = useState(null);

    // Fetch boundary data
    useEffect(() => {
        fetch("/DATA/OSMbased_adm0_boundary.json")
            .then((response) => response.json())
            .then((data) => setBoundaryData(data))
            .catch((error) => console.error("Error loading boundary data:", error));
    }, []);

    useEffect(() => {
        fetch("/DATA/OSMbased_adm1_boundary.json")
            .then((response) => response.json())
            .then((data) => setMunicipalityData(data))
            .catch((error) => console.error("Error loading municipality data:", error));
    }, []);


    const getHighchartsOptions = (chartData) => {

        return {
            title: { text: "Hourly Forecast" },
            chart: { type: "line", height: 500 },
            plotOptions: {
                series: {
                    lineWidth: 1
                }
            },
            xAxis: {
                categories: chartData.map(d => d.time), // Keep actual timestamps for precision
                title: { text: "" },
                labels: {
                    formatter: function () {
                        const date = new Date(this.value);
                        const hours = date.getHours();
                        if (hours === 0) {
                            return date.toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short"
                            });
                        }
                        return ""; // Empty label for other hours
                    },
                    rotation: 0,
                    step: 1,
                },
            },
            tooltip: {
                shared: true,
                xDateFormat: '%d %b, %H:%M',
            },
            legend: {
                layout: "vertical",
                align: "right",
                verticalAlign: "middle",
                floating: false,
            },
            // yAxis: [
            //     {
            //         title: { text: "Temperature", style: { fontSize: "12px" } },
            //         labels: {
            //             format: "{value}°C",
            //             style: { fontSize: "10px" },
            //             x: -5, // pull labels closer to axis
            //         },
            //         min: 0,
            //         tickInterval: 5,
            //     },
            //     {
            //         title: { text: "Cloud Cover", style: { fontSize: "12px" } },
            //         labels: {
            //             format: "{value}%",
            //             style: { fontSize: "10px" },
            //             x: -5,
            //         },
            //         opposite: false,
            //     },
            //     {
            //         title: { text: "CAPE", style: { fontSize: "12px" } },
            //         labels: {
            //             format: "{value}",
            //             style: { fontSize: "10px" },
            //             x: -5,
            //         },
            //         opposite: false,
            //     },
            //     {
            //         title: { text: "Wind Speed", style: { fontSize: "12px" } },
            //         labels: {
            //             format: "{value}",
            //             style: { fontSize: "10px" },
            //             x: -5,
            //         },
            //         opposite: false,
            //     },
            //     {
            //         title: { text: "Wind Direction", style: { fontSize: "12px" } },
            //         labels: {
            //             format: "{value}°",
            //             style: { fontSize: "10px" },
            //             x: -5,
            //         },
            //         min: 0,
            //         tickInterval: 80,
            //         opposite: false,
            //     },
            // ],
            yAxis: [
                {
                    title: { text: "Temperature", style: { fontSize: "12px" } },
                    labels: { format: "{value}°C", style: { fontSize: "10px" }, x: -5 },
                    min: 0,
                    tickInterval: 5,
                    showEmpty: false
                },
                {
                    title: { text: "Cloud Cover", style: { fontSize: "12px" } },
                    labels: { format: "{value}%", style: { fontSize: "10px" }, x: -5 },
                    opposite: false,
                    showEmpty: false
                },
                {
                    title: { text: "CAPE", style: { fontSize: "12px" } },
                    labels: { format: "{value}", style: { fontSize: "10px" }, x: -5 },
                    opposite: false,
                    showEmpty: false
                },
                {
                    title: { text: "Wind Speed", style: { fontSize: "12px" } },
                    labels: { format: "{value}", style: { fontSize: "10px" }, x: -5 },
                    opposite: false,
                    showEmpty: false
                },
                {
                    title: { text: "Wind Direction", style: { fontSize: "12px" } },
                    labels: { format: "{value}°", style: { fontSize: "10px" }, x: -5 },
                    min: 0,
                    tickInterval: 80,
                    opposite: false,
                    showEmpty: false
                },
                {
                    title: { text: "Rainfall (mm)", style: { fontSize: "12px" } },
                    labels: { format: "{value} mm", style: { fontSize: "10px" }, x: -5 },
                    min: 0,
                    tickInterval: 1,
                    opposite: false,
                    showEmpty: false,
                },
            ],            
            series: [
                {
                    name: "Temperature (°C)",
                    yAxis: 0,
                    data: chartData.map(d => d.temperature_2m),
                    color: "#8884d8",
                },
                {
                    name: "Cloud Cover (%)",
                    yAxis: 1,
                    data: chartData.map(d => d.cloud_cover),
                    color: "#82ca9d",
                },
                {
                    name: "CAPE",
                    yAxis: 2,
                    data: chartData.map(d => d.cape),
                    color: "#ffc658",
                },
                {
                    name: "Low Cloud (%)",
                    yAxis: 1,
                    data: chartData.map(d => d.cloud_cover_low),
                    color: "#ff7300",
                },
                {
                    name: "Mid Cloud (%)",
                    yAxis: 1,
                    data: chartData.map(d => d.cloud_cover_mid),
                    color: "#0073ff",
                },
                {
                    name: "High Cloud (%)",
                    yAxis: 1,
                    data: chartData.map(d => d.cloud_cover_high),
                    color: "#d84ee3",
                },
                {
                    name: "Wind Speed (km/h)",
                    yAxis: 3,
                    data: chartData.map(d => parseFloat(d.wind_speed_10m.toFixed(2))),
                    color: "#00c49f",
                },
                {
                    name: "Wind Direction (°)",
                    yAxis: 4,
                    data: chartData.map(d => d.wind_direction_10m),
                    color: "#ff0000",
                },
                {
                    name: "Rainfall (mm)",
                    yAxis: 5,
                    data: chartData.map(d => d.rain),
                    color: "#1e90ff",
                    type: "column",
                },
            ],
        };
    };


    return (
        <>
        {/* <div>
            <Lightningsuco/>
        </div> */}
        <div className="mapcontainerall">
            <MapContainer center={center} zoom={7.5} style={{ height: "100%", width: "100%" }}>
              <MapHomeControl />
              <MapInfoControl text="Lightning potential forecast based on Open-Meteo (JMA) model data. Click a location for details." />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <LayersControl position="topright">
                    {boundaryData && (
                        <LayersControl.BaseLayer name="Country" checked>
                            <GeoJSON
                                data={boundaryData}
                                style={{ color: "#ff7800", weight: 2, fillOpacity: 0.1 }}
                            />
                        </LayersControl.BaseLayer>
                    )}
                    {municipalityData && (
                        <LayersControl.BaseLayer name="Municipality">
                            <GeoJSON
                                data={municipalityData}
                                style={{ color: "#0066cc", weight: 1, fillOpacity: 0.2 }}
                            />
                        </LayersControl.BaseLayer>
                    )}
                </LayersControl>
                <ClickHandler setClickedData={setClickedData} />
                {clickedData && <Marker position={[clickedData.lat, clickedData.lng]} />}
            </MapContainer>

            {clickedData && (
                <div className="openmeteocharts" style={{ marginTop: "30px" }}>
                    <h3>
                        Forecast for {clickedData.lat.toFixed(2)}°, {clickedData.lng.toFixed(2)}°
                    </h3>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={getHighchartsOptions(clickedData.chartData)}
                    />
                </div>
            )}
        </div>
        </>
    );
};

export default OpenmeteoJMA;


